// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';
import * as ConfigurationCore from './configuration.core';

export function isTestItemModel(source: any): source is ITestItem {
    // Method Implementation: simple
    return source.modelName === 'testItem';
}

export interface TestItemZodSchemaShape {
    testA?: string;

    testB?: number;

    typeC?: number;
}

// Artifact Implementation: artifacts/zodSchemaType
export const TestItemZodSchema: Zod.ZodSchema<TestItemZodSchemaShape> = Zod.lazy(() => Zod.object({
    testA: Zod.string().optional(),
    testB: Zod.number().optional(),
    typeC: Zod.number().optional()
}).nonstrict());

export interface ITestItem extends Wellspring.IModel {
    readonly modelName: string;

    readonly testA: string;

    readonly testB: number;

    readonly typeC: number;

    readonly isTestADefined: boolean;

    readonly isTestBDefined: boolean;

    readonly isTypeCDefined: boolean;
}

export class TestItem implements ITestItem {
    public static readonly modelName: string = 'testItem';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'testA', 'testB', 'typeC' ];

    public get modelName(): string {
        // Method Implementation: simple
        return TestItem.modelName;
    }

    private _testA: string;

    private _testB: number;

    private _typeC: number;

    public get testA(): string {
        // Method Implementation: simple
        return this._testA;
    }

    public get testB(): number {
        // Method Implementation: simple
        return this._testB;
    }

    public get typeC(): number {
        // Method Implementation: simple
        return this._typeC;
    }

    public set testA(value: string) {
        // Method Implementation: simple
        this._testA = value;
        this._isTestADefined = true;
    }

    public set testB(value: number) {
        // Method Implementation: simple
        this._testB = value;
        this._isTestBDefined = true;
    }

    public set typeC(value: number) {
        // Method Implementation: simple
        this._typeC = value;
        this._isTypeCDefined = true;
    }

    private _isTestADefined: boolean;

    private _isTestBDefined: boolean;

    private _isTypeCDefined: boolean;

    public get isTestADefined(): boolean {
        // Method Implementation: simple
        return this._isTestADefined;
    }

    public get isTestBDefined(): boolean {
        // Method Implementation: simple
        return this._isTestBDefined;
    }

    public get isTypeCDefined(): boolean {
        // Method Implementation: simple
        return this._isTypeCDefined;
    }

    public constructor();
    public constructor(source: ITestItem);
    public constructor(source: Partial<Pick<TestItem, 'testA' | 'testB' | 'typeC'>>);
    public constructor(source?: ITestItem | Partial<Pick<TestItem, 'testA' | 'testB' | 'typeC'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isTestItemModel(source)) {
                if (source.isTestADefined) {
                    this._testA = source.testA;
                    this._isTestADefined = true;
                }
                else {
                    this._testA = '';
                    this._isTestADefined = false;
                }
                
                if (source.isTestBDefined) {
                    this._testB = source.testB;
                    this._isTestBDefined = true;
                }
                else {
                    this._testB = 0;
                    this._isTestBDefined = false;
                }
                
                if (source.isTypeCDefined) {
                    this._typeC = source.typeC;
                    this._isTypeCDefined = true;
                }
                else {
                    this._typeC = 0;
                    this._isTypeCDefined = false;
                }
            }
            else {
                if (source.testA !== undefined) {
                    this._testA = source.testA;
                    this._isTestADefined = true;
                }
                else {
                    this._testA = '';
                    this._isTestADefined = false;
                }
                
                if (source.testB !== undefined) {
                    this._testB = source.testB;
                    this._isTestBDefined = true;
                }
                else {
                    this._testB = 0;
                    this._isTestBDefined = false;
                }
                
                if (source.typeC !== undefined) {
                    this._typeC = source.typeC;
                    this._isTypeCDefined = true;
                }
                else {
                    this._typeC = 0;
                    this._isTypeCDefined = false;
                }
            }
        }
        else {
            this._testA = '';
            this._isTestADefined = false;
        
            this._testB = 0;
            this._isTestBDefined = false;
        
            this._typeC = 0;
            this._isTypeCDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return TestItem.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isTestADefined && this.testA !== undefined)
            definedProperties.push('testA');
        if (this.isTestBDefined && this.testB !== undefined)
            definedProperties.push('testB');
        if (this.isTypeCDefined && this.typeC !== undefined)
            definedProperties.push('typeC');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'testA':
                return this.testA;
            case 'testB':
                return this.testB;
            case 'typeC':
                return this.typeC;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isTestADefined && this.testA !== undefined) {
            if (this.testA !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('testA', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.testA);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('testA', 'null'));
            }
        }
        
        if (this.isTestBDefined && this.testB !== undefined) {
            if (this.testB !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('testB', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.testB);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('testB', 'null'));
            }
        }
        
        if (this.isTypeCDefined && this.typeC !== undefined) {
            if (this.typeC !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('typeC', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.typeC);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('typeC', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'testA':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.testA = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'testA' cannot be null`);
                    this.testA = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'testA'`);
                }
        
                return true;
            case 'testB':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.testB = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'testB' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'testB'`);
                }
        
                return true;
            case 'typeC':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.typeC = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.typeC = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'typeC' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'typeC'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // testA
        if (this.isTestADefined && this.testA !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.testA === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.testA);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // testB
        if (this.isTestBDefined && this.testB !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.testB);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // typeC
        if (this.isTypeCDefined && this.typeC !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.typeC);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // testA
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.testA = <any>null;
                    break;
                case 1:
                    this.testA = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // testB
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.testB = reader.readIntegerValue();
        }
        
        // typeC
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.typeC = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): TestItem {
        // Method Implementation: simple
        const schema = TestItemZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: TestItemZodSchemaShape): TestItem {
        // Method Implementation: parseZodSchema
        return new TestItem({
            testA: schema.testA,
            testB: schema.testB,
            typeC: schema.typeC
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            testA: this.isTestADefined ? ((this.testA === null ? undefined : this.testA)) : undefined,
            testB: this.isTestBDefined ? (this.testB) : undefined,
            typeC: this.isTypeCDefined ? (this.typeC) : undefined,
        };
    }

    public clone(): TestItem {
        // Method Implementation: clone
        return new TestItem({
            testA: this.isTestADefined ? this.testA : undefined,
            testB: this.isTestBDefined ? this.testB : undefined,
            typeC: this.isTypeCDefined ? this.typeC : undefined
        });
    }
}

export interface IDebugService {
    entanglement(): Promise<void>;

    testMacro(entityKey: string, macro: string): Promise<ConfigurationCore.MacroTestResult>;

    testMacro(entityKey: string, macro: string, qualifier: string): Promise<ConfigurationCore.MacroTestResult>;
}

export class DebugServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public entanglement(): Promise<void>;
    public entanglement(actionContext: Wellspring.ActionContext): Promise<void>;
    public async entanglement(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
        ]).check(args)) {
            const [  ] = args;
        
            const activity = new Wellspring.DistributedActivity('debug.entanglement');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('entanglement', 'utf8'));
                requestMetadata.add('service', Buffer.from('debug', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("c5352f7e7da8235022704b9753054b6ee911e25816cb476546a0b5bf12e5a827");
        
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `debug[${this.identifier}].entanglement`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "debug/entanglement" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `debug[${this.identifier}].entanglement`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `debug[${this.identifier}].entanglement`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public testMacro(entityKey: string, macro: string): Promise<ConfigurationCore.MacroTestResult>;
    public testMacro(actionContext: Wellspring.ActionContext, entityKey: string, macro: string): Promise<ConfigurationCore.MacroTestResult>;
    public testMacro(entityKey: string, macro: string, qualifier: string): Promise<ConfigurationCore.MacroTestResult>;
    public testMacro(actionContext: Wellspring.ActionContext, entityKey: string, macro: string, qualifier: string): Promise<ConfigurationCore.MacroTestResult>;
    public async testMacro(...args: unknown[]): Promise<ConfigurationCore.MacroTestResult> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"string"}
            Zod.string(), 
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ entityKey, macro ] = args;
        
            const activity = new Wellspring.DistributedActivity('debug.testMacro');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('testMacro', 'utf8'));
                requestMetadata.add('service', Buffer.from('debug', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(1);
                    writer.writeStringValue("ce1afab362a24cebec6a65ae80d5105e8939b65e13845a7551c927bde10c189f");
        
                    if (entityKey && entityKey !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (entityKey === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(entityKey);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (macro && macro !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (macro === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(macro);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ConfigurationCore.MacroTestResult>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `debug[${this.identifier}].testMacro`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entityKey: JSON.parse(JSON.stringify(entityKey)),
                                macro: JSON.parse(JSON.stringify(macro))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "debug/testMacro" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: ConfigurationCore.MacroTestResult;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ConfigurationCore.MacroTestResult();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `debug[${this.identifier}].testMacro`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entityKey: JSON.parse(JSON.stringify(entityKey)),
                        macro: JSON.parse(JSON.stringify(macro))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `debug[${this.identifier}].testMacro`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entityKey: JSON.parse(JSON.stringify(entityKey)),
                        macro: JSON.parse(JSON.stringify(macro))
                    },
                    error
                });
        
                throw error;
            });
        }
        else if (Zod.tuple([
            // {"template":"string"}
            Zod.string(), 
            // {"template":"string"}
            Zod.string(), 
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ entityKey, macro, qualifier ] = args;
        
            const activity = new Wellspring.DistributedActivity('debug.testMacro');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('testMacro', 'utf8'));
                requestMetadata.add('service', Buffer.from('debug', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(2);
                    writer.writeStringValue("0ed433e1e3f556c0e5de4fad319bc3b40c4c7d23c31f2517198de3b19ed5e8f1");
        
                    if (entityKey && entityKey !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (entityKey === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(entityKey);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (macro && macro !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (macro === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(macro);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (qualifier && qualifier !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (qualifier === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(qualifier);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ConfigurationCore.MacroTestResult>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `debug[${this.identifier}].testMacro`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entityKey: JSON.parse(JSON.stringify(entityKey)),
                                macro: JSON.parse(JSON.stringify(macro)),
                                qualifier: JSON.parse(JSON.stringify(qualifier))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "debug/testMacro" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: ConfigurationCore.MacroTestResult;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ConfigurationCore.MacroTestResult();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `debug[${this.identifier}].testMacro`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entityKey: JSON.parse(JSON.stringify(entityKey)),
                        macro: JSON.parse(JSON.stringify(macro)),
                        qualifier: JSON.parse(JSON.stringify(qualifier))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `debug[${this.identifier}].testMacro`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entityKey: JSON.parse(JSON.stringify(entityKey)),
                        macro: JSON.parse(JSON.stringify(macro)),
                        qualifier: JSON.parse(JSON.stringify(qualifier))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'testItem':
                return new TestItem();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'testItem':
                return new TestItem(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'testItem':
                return TestItem.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
